import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Wrench, Loader2, Search, Building2, Calendar, FileText, User, Phone, Mail, Briefcase,
  DollarSign, Clock, Eye, Receipt, File, ChevronDown, ChevronRight, Plus, CheckCircle, X,
  UserCheck, Users, ShoppingCart, Upload, Trash2, Share2, UserPlus, Download, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import ServiceDailyReports from '../../components/ServiceDailyReports';
import ServiceNotes from '../../components/ServiceNotes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BranchServices = ({ branchIdOverride, readOnly = false }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('aktif');
  
  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState({});
  const [approvingHakedis, setApprovingHakedis] = useState(null);
  
  // New hakediş modal state
  const [showHakedisModal, setShowHakedisModal] = useState(false);
  const [selectedServiceForHakedis, setSelectedServiceForHakedis] = useState(null);
  const [newHakedis, setNewHakedis] = useState({
    tarih: new Date().toISOString().split('T')[0],
    hizmetler: [{ ad: '', miktar: '' }],
    miktar: '',
    aciklama: '',
    belgeler: []
  });
  const [savingHakedis, setSavingHakedis] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // User selection modal state (Kişi Seçimi)
  const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
  const [selectedServiceForUsers, setSelectedServiceForUsers] = useState(null);
  const [workPanelUsers, setWorkPanelUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [contractViewers, setContractViewers] = useState([]);
  const [dailyReportUsers, setDailyReportUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [savingUsers, setSavingUsers] = useState(false);
  const [detailTab, setDetailTab] = useState('genel');

  // Currency symbols
  const currencySymbols = {
    'TRY': '₺',
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  };

  // Status labels and colors
  const statusLabels = {
    'aktif': 'Aktif',
    'tamamlandi': 'Tamamlandı',
    'iptal': 'İptal',
    'beklemede': 'Beklemede'
  };

  const statusColors = {
    'aktif': 'bg-green-100 text-green-700',
    'tamamlandi': 'bg-blue-100 text-blue-700',
    'iptal': 'bg-red-100 text-red-700',
    'beklemede': 'bg-yellow-100 text-yellow-700'
  };

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/branch/services`, { headers: getAuthHeaders() });
      setServices(res.data || []);
    } catch (error) {
      console.error('Hizmetler yüklenirken hata:', error);
      toast.error('Hizmetler yüklenemedi');
    }
    setLoading(false);
  };

  // Mark service as seen
  const markServiceAsSeen = async (serviceId) => {
    try {
      await axios.post(`${API}/branch/services/${serviceId}/mark-seen`, {}, { headers: getAuthHeaders() });
      // Update local state to remove YENİ badge
      setServices(prev => prev.map(s => 
        s.id === serviceId ? { ...s, is_new: false, has_updates: false } : s
      ));
      // Dispatch event to update sidebar count
      window.dispatchEvent(new CustomEvent('refreshServicesCount'));
    } catch (error) {
      console.error('Mark seen hatası:', error);
    }
  };

  const openDetailModal = async (service) => {
    try {
      // Mark as seen when opening detail
      if (service.is_new || service.has_updates) {
        markServiceAsSeen(service.id);
      }
      const res = await axios.get(`${API}/branch/services/${service.id}`, { headers: getAuthHeaders() });
      setSelectedService(res.data);
      setDetailTab('genel');
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Hizmet detayı yüklenirken hata:', error);
      toast.error('Hizmet detayı yüklenemedi');
    }
  };

  // Toggle expanded row and mark as seen
  const toggleRowExpand = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service && (service.is_new || service.has_updates)) {
      markServiceAsSeen(serviceId);
    }
    setExpandedRows(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  // Hakediş onaylama (Şube Yönetim)
  const handleApproveHakedis = async (serviceId, hakedisId) => {
    setApprovingHakedis(`${hakedisId}-sube_yonetim`);
    try {
      await axios.post(
        `${API}/purchasing/services/${serviceId}/hakedis/${hakedisId}/approve`,
        { approval_type: 'sube_yonetim' },
        { headers: getAuthHeaders() }
      );
      toast.success('Şube Yönetim onayı verildi');
      loadServices(); // Listeyi yenile
    } catch (err) {
      console.error('Onay hatası:', err);
      toast.error('Onay verilemedi');
    } finally {
      setApprovingHakedis(null);
    }
  };

  // Hakediş onayını geri alma
  const handleRevokeApproval = async (serviceId, hakedisId) => {
    setApprovingHakedis(`${hakedisId}-sube_yonetim-revoke`);
    try {
      await axios.post(
        `${API}/purchasing/services/${serviceId}/hakedis/${hakedisId}/revoke-approval`,
        { approval_type: 'sube_yonetim' },
        { headers: getAuthHeaders() }
      );
      toast.success('Onay geri alındı');
      loadServices();
    } catch (err) {
      console.error('Onay geri alma hatası:', err);
      toast.error('Onay geri alınamadı');
    } finally {
      setApprovingHakedis(null);
    }
  };

  // Open hakediş modal
  const openHakedisModal = (service) => {
    setSelectedServiceForHakedis(service);
    setNewHakedis({
      tarih: new Date().toISOString().split('T')[0],
      hizmetler: [{ ad: '', miktar: '' }],
      miktar: '',
      aciklama: '',
      belgeler: []
    });
    setShowHakedisModal(true);
  };

  // Hizmet satırı ekle/güncelle/sil
  const addHizmet = () => {
    setNewHakedis(prev => ({ ...prev, hizmetler: [...prev.hizmetler, { ad: '', miktar: '' }] }));
  };
  const updateHizmet = (index, field, value) => {
    setNewHakedis(prev => ({
      ...prev,
      hizmetler: prev.hizmetler.map((h, i) => i === index ? { ...h, [field]: value } : h)
    }));
  };
  const removeHizmet = (index) => {
    setNewHakedis(prev => ({
      ...prev,
      hizmetler: prev.hizmetler.length > 1 ? prev.hizmetler.filter((_, i) => i !== index) : prev.hizmetler
    }));
  };

  // Handle file upload for hakediş
  const handleHakedisFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingFile(true);
    const formData = new FormData();
    formData.append('files', file);
    
    try {
      const res = await axios.post(`${API}/upload/offers`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const uploadedFile = res.data.files?.[0];
      if (uploadedFile) {
        setNewHakedis(prev => ({
          ...prev,
          belgeler: [...prev.belgeler, {
            filename: uploadedFile.filename || file.name,
            url: uploadedFile.url
          }]
        }));
        toast.success('Dosya yüklendi');
      } else {
        toast.error('Dosya yüklenemedi');
      }
    } catch (err) {
      console.error('Dosya yükleme hatası:', err);
      toast.error('Dosya yüklenemedi');
    } finally {
      setUploadingFile(false);
    }
  };

  // Remove file from hakediş
  const removeHakedisFile = (index) => {
    setNewHakedis(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  // Open user selection modal (Kişi Seçimi)
  const openUserSelectionModal = async (service) => {
    setSelectedServiceForUsers(service);
    setLoadingUsers(true);
    setShowUserSelectionModal(true);
    
    try {
      // Fetch work panel users
      const res = await axios.get(`${API}/branch/work-panel-users`, { headers: getAuthHeaders() });
      setWorkPanelUsers(res.data || []);
      
      // Set currently selected users
      const currentUsers = service.visible_to_users || [];
      setSelectedUsers(currentUsers);
      setContractViewers(service.contract_viewers || []);
      setDailyReportUsers(service.daily_report_users || []);
    } catch (err) {
      console.error('Kullanıcılar yüklenirken hata:', err);
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        // Kişi kaldırılınca sözleşme görüntüleme + rapor sorumluluğu da kaldırılır
        setContractViewers(cv => cv.filter(id => id !== userId));
        setDailyReportUsers(dr => dr.filter(id => id !== userId));
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Toggle daily report responsibility for a user
  const toggleDailyReportUser = (userId) => {
    setDailyReportUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Toggle contract document visibility for a user
  const toggleContractViewer = (userId) => {
    setContractViewers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Select all users
  const selectAllUsers = () => {
    setSelectedUsers(workPanelUsers.map(u => u.id));
  };

  // Deselect all users
  const deselectAllUsers = () => {
    setSelectedUsers([]);
    setContractViewers([]);
  };

  // Save selected users
  const handleSaveSelectedUsers = async () => {
    if (!selectedServiceForUsers) return;
    
    setSavingUsers(true);
    try {
      await axios.put(
        `${API}/branch/services/${selectedServiceForUsers.id}/set-work-panel-users`,
        {
          user_ids: selectedUsers,
          contract_viewers: contractViewers.filter(id => selectedUsers.includes(id)),
          daily_report_users: dailyReportUsers.filter(id => selectedUsers.includes(id))
        },
        { headers: getAuthHeaders() }
      );
      
      const count = selectedUsers.length;
      if (count === 0) {
        toast.success('Hizmet paylaşımı kaldırıldı');
      } else {
        toast.success(`Hizmet ${count} kişiyle paylaşıldı`);
      }
      
      setShowUserSelectionModal(false);
      loadServices();
    } catch (err) {
      console.error('Kullanıcılar kaydedilirken hata:', err);
      toast.error('İşlem başarısız');
    } finally {
      setSavingUsers(false);
    }
  };

  // Save new hakediş
  const handleSaveHakedis = async () => {
    if (!newHakedis.tarih) {
      toast.error('Lütfen tarih alanını doldurun');
      return;
    }
    
    setSavingHakedis(true);
    try {
      // Get current service data
      const serviceRes = await axios.get(
        `${API}/branch/services/${selectedServiceForHakedis.id}`,
        { headers: getAuthHeaders() }
      );
      const service = serviceRes.data;
      
      const validHizmetler = (newHakedis.hizmetler || []).filter(h => h.ad && h.ad.trim());
      const miktarSummary = validHizmetler.length > 0
        ? validHizmetler.map(h => `${h.ad.trim()}${h.miktar && h.miktar.trim() ? ': ' + h.miktar.trim() : ''}`).join(', ')
        : newHakedis.miktar;
      
      // Create new hakediş with ID
      const hakedisId = Date.now().toString();
      const hakedisData = {
        id: hakedisId,
        tarih: newHakedis.tarih,
        miktar: miktarSummary,
        hizmetler: validHizmetler,
        aciklama: newHakedis.aciklama,
        durum: 'beklemede',
        belgeler: newHakedis.belgeler,
        sube_yonetim_onay: false,
        yonetim_kurulu_onay: false,
        satin_alma_onay: false
      };
      
      // Update service with new hakediş
      const updatedHakedisler = [...(service.hakedisler || []), hakedisData];
      
      await axios.put(
        `${API}/purchasing/services/${selectedServiceForHakedis.id}`,
        { hakedisler: updatedHakedisler },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Hakediş eklendi');
      setShowHakedisModal(false);
      loadServices();
    } catch (err) {
      console.error('Hakediş kaydetme hatası:', err);
      toast.error('Hakediş kaydedilemedi');
    } finally {
      setSavingHakedis(false);
    }
  };

  const filteredServices = services.filter(service => {
    // Önce tab'a göre filtrele
    if (activeTab === 'aktif' && service.status !== 'aktif') return false;
    if (activeTab === 'tamamlandi' && service.status !== 'tamamlandi') return false;
    
    // Sonra arama terimine göre filtrele
    const search = searchTerm.toLowerCase();
    return (
      (service.service_name || '').toLowerCase().includes(search) ||
      (service.service_no || '').toLowerCase().includes(search) ||
      (service.cari_hesap_name || '').toLowerCase().includes(search) ||
      (service.description || '').toLowerCase().includes(search)
    );
  });

  // Stats
  const stats = {
    total: services.length,
    aktif: services.filter(s => s.status === 'aktif').length,
    tamamlandi: services.filter(s => s.status === 'tamamlandi').length,
    beklemede: services.filter(s => s.status === 'beklemede').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="branch-services-loading">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        <span className="ml-2 text-gray-500">Yükleniyor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branch-services-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wrench className="h-8 w-8 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alınan Hizmetler</h1>
            <p className="text-gray-500 text-sm">Şubenize ait hizmet alımları</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Toplam</p>
                <p className="text-2xl font-bold text-purple-700">{stats.total}</p>
              </div>
              <Wrench className="h-10 w-10 text-purple-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Aktif</p>
                <p className="text-2xl font-bold text-green-700">{stats.aktif}</p>
              </div>
              <Clock className="h-10 w-10 text-green-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tamamlandı</p>
                <p className="text-2xl font-bold text-blue-700">{stats.tamamlandi}</p>
              </div>
              <FileText className="h-10 w-10 text-blue-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-white border-yellow-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Beklemede</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.beklemede}</p>
              </div>
              <Calendar className="h-10 w-10 text-yellow-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Hizmet ara (isim, firma, açıklama...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-services-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-amber-600" />
              Hizmet Listesi
            </CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="aktif" className="gap-2">
                  <Clock className="h-4 w-4" />
                  Aktif ({stats.aktif})
                </TabsTrigger>
                <TabsTrigger value="tamamlandi" className="gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Tamamlanan ({stats.tamamlandi})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {filteredServices.length === 0 ? (
            <div className="text-center py-12" data-testid="no-services-message">
              <Wrench className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">
                {activeTab === 'aktif' ? 'Aktif hizmet bulunmuyor' : 'Tamamlanan hizmet bulunmuyor'}
              </p>
              <p className="text-gray-400 text-sm mt-1">
                {activeTab === 'aktif' 
                  ? 'Satın alma biriminden hizmet eklendiğinde burada görüntülenecektir'
                  : 'Tamamlanan hizmetler burada görüntülenecektir'}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Hizmet No</TableHead>
                    <TableHead>Hizmet Adı</TableHead>
                    <TableHead>Firma</TableHead>
                    <TableHead>Tutar</TableHead>
                    <TableHead>Hakediş</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>Sorumlular</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredServices.map((service) => (
                    <React.Fragment key={service.id}>
                      {/* Ana satır */}
                      <TableRow 
                        className={`cursor-pointer hover:bg-amber-50 transition-colors ${expandedRows[service.id] ? 'bg-amber-50' : ''} ${(service.is_new || service.has_updates) ? 'ring-2 ring-red-400 ring-inset bg-red-50/30' : ''}`}
                        onClick={() => toggleRowExpand(service.id)}
                        data-testid={`service-row-${service.id}`}
                      >
                        <TableCell className="w-8">
                          {expandedRows[service.id] ? (
                            <ChevronDown className="h-4 w-4 text-amber-600" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{service.service_no || '-'}</Badge>
                            {service.is_new && (
                              <Badge className="bg-red-500 text-white animate-pulse text-xs">YENİ</Badge>
                            )}
                            {!service.is_new && service.has_updates && (
                              <Badge className="bg-orange-500 text-white animate-pulse text-xs">GÜNCELLEME</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {service.service_name || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span>{service.cari_hesap_name || '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-green-600">
                            {(service.total_amount || 0).toLocaleString('tr-TR')} {currencySymbols[service.currency] || '₺'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            {service.hakedisler?.length || 0} adet
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[service.status] || 'bg-gray-100'}>
                            {statusLabels[service.status] || service.status || 'Beklemede'}
                          </Badge>
                        </TableCell>
                        <TableCell data-testid={`service-people-${service.id}`}>
                          {service.visible_to_user_names?.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {service.visible_to_user_names.slice(0, 4).map((nm, ni) => (
                                <span key={ni} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[11px]">{nm}</span>
                              ))}
                              {service.visible_to_user_names.length > 4 && (
                                <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[11px]">+{service.visible_to_user_names.length - 4}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant={(service.visible_to_users?.length > 0) ? "default" : "outline"}
                              size="sm"
                              className={`gap-1 ${(service.visible_to_users?.length > 0)
                                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                : 'text-blue-600 border-blue-300 hover:bg-blue-50'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openUserSelectionModal(service);
                              }}
                              data-testid={`select-users-btn-${service.id}`}
                              disabled={service.status === 'tamamlandi'}
                            >
                              <UserPlus className="h-3 w-3" />
                              {(service.visible_to_users?.length > 0) 
                                ? `${service.visible_to_users.length} Kişi` 
                                : 'Kişi Seç'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1 text-green-600 border-green-300 hover:bg-green-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                openHakedisModal(service);
                              }}
                              data-testid={`add-hakedis-btn-${service.id}`}
                              disabled={service.status === 'tamamlandi'}
                            >
                              <Plus className="h-3 w-3" />
                              Hakediş
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetailModal(service);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expandable içerik - Hakedişler */}
                      {expandedRows[service.id] && (
                        <TableRow>
                          <TableCell colSpan={9} className="bg-gray-50 p-0">
                            <div className="p-4">
                              {service.hakedisler && service.hakedisler.length > 0 ? (
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                                      <Receipt className="h-4 w-4 text-green-600" />
                                      Hakedişler ({service.hakedisler.length})
                                    </h4>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="gap-1 text-green-600 border-green-300 hover:bg-green-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openHakedisModal(service);
                                      }}
                                    >
                                      <Plus className="h-3 w-3" />
                                      Yeni Hakediş Ekle
                                    </Button>
                                  </div>
                                  <div className="grid gap-3">
                                    {service.hakedisler.map((hakedis, index) => (
                                      <div 
                                        key={hakedis.id || index} 
                                        className="bg-white rounded-lg border p-4 shadow-sm"
                                        data-testid={`hakedis-row-${hakedis.id}`}
                                      >
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                              <span className="font-semibold text-green-700">
                                                {index + 1}. Hakediş
                                              </span>
                                              <Badge className={
                                                hakedis.durum === 'odendi' ? 'bg-green-100 text-green-700' :
                                                hakedis.durum === 'onaylandi' ? 'bg-blue-100 text-blue-700' :
                                                hakedis.durum === 'iptal' ? 'bg-red-100 text-red-700' :
                                                'bg-yellow-100 text-yellow-700'
                                              }>
                                                {hakedis.durum === 'odendi' ? 'Ödendi' :
                                                 hakedis.durum === 'onaylandi' ? 'Onaylandı' :
                                                 hakedis.durum === 'iptal' ? 'İptal' : 'Beklemede'}
                                              </Badge>
                                            </div>
                                            <div className="grid grid-cols-4 gap-4 text-sm">
                                              <div>
                                                <span className="text-gray-500">Tarih:</span>
                                                <p className="font-medium">
                                                  {hakedis.tarih ? new Date(hakedis.tarih).toLocaleDateString('tr-TR') : '-'}
                                                </p>
                                              </div>
                                              <div>
                                                <span className="text-gray-500">Hizmetler:</span>
                                                {hakedis.hizmetler && hakedis.hizmetler.length > 0 ? (
                                                  <div className="space-y-0.5 mt-0.5">
                                                    {hakedis.hizmetler.map((h, hi) => (
                                                      <p key={hi} className="font-medium text-xs">
                                                        • {h.ad}{h.miktar ? <span className="text-gray-500"> — {h.miktar}</span> : null}
                                                      </p>
                                                    ))}
                                                  </div>
                                                ) : (
                                                  <p className="font-medium">{hakedis.miktar || '-'}</p>
                                                )}
                                              </div>
                                              {parseFloat(hakedis.tutar || 0) > 0 && (
                                                <div>
                                                  <span className="text-gray-500">Tutar:</span>
                                                  <p className="font-semibold text-green-600">
                                                    {parseFloat(hakedis.tutar || 0).toLocaleString('tr-TR')} {currencySymbols[service.currency] || '₺'}
                                                  </p>
                                                </div>
                                              )}
                                              <div>
                                                <span className="text-gray-500">Açıklama:</span>
                                                <p className="font-medium">{hakedis.aciklama || '-'}</p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Belgeler */}
                                        {hakedis.belgeler && hakedis.belgeler.length > 0 && (
                                          <div className="mt-3 pt-3 border-t">
                                            <span className="text-sm font-medium text-gray-700 flex items-center gap-1 mb-2">
                                              <FileText className="h-4 w-4 text-blue-600" />
                                              Belgeler ({hakedis.belgeler.length})
                                            </span>
                                            <div className="flex flex-wrap gap-2">
                                              {hakedis.belgeler.map((doc, docIdx) => (
                                                <a
                                                  key={docIdx}
                                                  href={doc.url?.startsWith('/api') ? `${BACKEND_URL}${doc.url}` : doc.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 rounded px-3 py-1.5 text-sm transition-colors"
                                                  data-testid={`hakedis-doc-${hakedis.id}-${docIdx}`}
                                                >
                                                  <Download className="h-3 w-3 text-blue-600" />
                                                  <span className="text-blue-700">{doc.filename || 'Belge'}</span>
                                                </a>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Onay Butonları */}
                                        <div className="mt-4 pt-4 border-t flex items-center gap-6">
                                          {/* Şube Yönetim Onayı */}
                                          <div className="flex items-center gap-2">
                                            <div className="flex items-center gap-1">
                                              <UserCheck className="h-4 w-4 text-amber-600" />
                                              <span className="text-sm font-medium text-gray-700">Şube Yönetim</span>
                                            </div>
                                            {hakedis.sube_yonetim_onay ? (
                                              <div className="flex items-center gap-1">
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                                <span className="text-xs text-green-600">
                                                  {hakedis.sube_yonetim_onay_yapan && `(${hakedis.sube_yonetim_onay_yapan})`}
                                                </span>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRevokeApproval(service.id, hakedis.id);
                                                  }}
                                                  disabled={approvingHakedis === `${hakedis.id}-sube_yonetim-revoke`}
                                                >
                                                  {approvingHakedis === `${hakedis.id}-sube_yonetim-revoke` ? (
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                  ) : (
                                                    <X className="h-3 w-3" />
                                                  )}
                                                </Button>
                                              </div>
                                            ) : (
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleApproveHakedis(service.id, hakedis.id);
                                                }}
                                                disabled={approvingHakedis === `${hakedis.id}-sube_yonetim`}
                                                data-testid={`approve-sube-btn-${hakedis.id}`}
                                              >
                                                {approvingHakedis === `${hakedis.id}-sube_yonetim` ? (
                                                  <Loader2 className="h-3 w-3 animate-spin" />
                                                ) : (
                                                  <Plus className="h-3 w-3" />
                                                )}
                                                Onayla
                                              </Button>
                                            )}
                                          </div>
                                          
                                          {/* Yönetim Kurulu Onayı (Sadece görüntüleme) */}
                                          <div className="flex items-center gap-2">
                                            <div className="flex items-center gap-1">
                                              <Users className="h-4 w-4 text-purple-600" />
                                              <span className="text-sm font-medium text-gray-700">Yönetim Kurulu</span>
                                            </div>
                                            {hakedis.yonetim_kurulu_onay ? (
                                              <div className="flex items-center gap-1">
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                                <span className="text-xs text-green-600">
                                                  {hakedis.yonetim_kurulu_onay_yapan && `(${hakedis.yonetim_kurulu_onay_yapan})`}
                                                </span>
                                              </div>
                                            ) : (
                                              <span className="text-xs text-gray-400">Bekliyor</span>
                                            )}
                                          </div>
                                          
                                          {/* Satın Alma Onayı (Sadece görüntüleme) */}
                                          <div className="flex items-center gap-2">
                                            <div className="flex items-center gap-1">
                                              <ShoppingCart className="h-4 w-4 text-blue-600" />
                                              <span className="text-sm font-medium text-gray-700">Satın Alma</span>
                                            </div>
                                            {hakedis.satin_alma_onay ? (
                                              <div className="flex items-center gap-1">
                                                <CheckCircle className="h-5 w-5 text-green-500" />
                                                <span className="text-xs text-green-600">
                                                  {hakedis.satin_alma_onay_yapan && `(${hakedis.satin_alma_onay_yapan})`}
                                                </span>
                                              </div>
                                            ) : (
                                              <span className="text-xs text-gray-400">Bekliyor</span>
                                            )}
                                          </div>
                                          
                                          {/* Tam Onay Durumu */}
                                          {hakedis.sube_yonetim_onay && hakedis.yonetim_kurulu_onay && hakedis.satin_alma_onay && (
                                            <Badge className="ml-auto bg-green-100 text-green-700">
                                              <CheckCircle className="h-3 w-3 mr-1" />
                                              Tam Onaylı
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-6 text-gray-500">
                                  <Receipt className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                                  <p>Bu hizmet için henüz hakediş girilmemiş</p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-3 gap-1 text-green-600 border-green-300 hover:bg-green-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openHakedisModal(service);
                                    }}
                                  >
                                    <Plus className="h-3 w-3" />
                                    İlk Hakediş Ekle
                                  </Button>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-amber-600" />
              Hizmet Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedService && (
            <Tabs value={detailTab} onValueChange={setDetailTab} className="py-2">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="genel" data-testid="detail-tab-genel">Genel Bilgiler</TabsTrigger>
                <TabsTrigger value="gunluk" data-testid="detail-tab-gunluk">Günlük Rapor</TabsTrigger>
                <TabsTrigger value="notlar" data-testid="detail-tab-notlar">Günlük Notlar</TabsTrigger>
              </TabsList>
              <TabsContent value="genel">
              <div className="space-y-6 py-4" data-testid="service-detail-content">
              {/* Temel Bilgiler */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Hizmet No</Label>
                  <p className="font-mono font-semibold">{selectedService.service_no || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <Badge className={statusColors[selectedService.status] || 'bg-gray-100'}>
                    {statusLabels[selectedService.status] || selectedService.status || 'Beklemede'}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-gray-500">Hizmet Adı</Label>
                <p className="font-semibold">{selectedService.service_name || '-'}</p>
              </div>

              {selectedService.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p>{selectedService.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Firma</Label>
                  <p className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    {selectedService.cari_hesap_name || '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Şube</Label>
                  <p>{selectedService.branch_name || '-'}</p>
                </div>
              </div>

              {/* Yetkili Kişi Bilgileri */}
              {(selectedService.yetkili_adi || selectedService.yetkili_gorevi || selectedService.yetkili_telefon || selectedService.yetkili_email) && (
                <div className="border rounded-lg p-4 bg-amber-50/50">
                  <Label className="flex items-center gap-2 mb-3 text-amber-700 font-medium">
                    <User className="h-4 w-4" />
                    Yetkili Kişi Bilgileri
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedService.yetkili_adi && (
                      <div>
                        <Label className="text-gray-500 text-sm">Yetkili Adı</Label>
                        <p className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          {selectedService.yetkili_adi}
                        </p>
                      </div>
                    )}
                    {selectedService.yetkili_gorevi && (
                      <div>
                        <Label className="text-gray-500 text-sm">Görevi</Label>
                        <p className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-gray-400" />
                          {selectedService.yetkili_gorevi}
                        </p>
                      </div>
                    )}
                    {selectedService.yetkili_telefon && (
                      <div>
                        <Label className="text-gray-500 text-sm">Telefon</Label>
                        <p className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          {selectedService.yetkili_telefon}
                        </p>
                      </div>
                    )}
                    {selectedService.yetkili_email && (
                      <div>
                        <Label className="text-gray-500 text-sm">E-posta</Label>
                        <p className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-400" />
                          {selectedService.yetkili_email}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tarih ve Tutar Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Başlangıç Tarihi</Label>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {selectedService.start_date ? new Date(selectedService.start_date).toLocaleDateString('tr-TR') : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Bitiş Tarihi</Label>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {selectedService.end_date ? new Date(selectedService.end_date).toLocaleDateString('tr-TR') : '-'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Toplam Tutar</Label>
                  <p className="text-xl font-bold text-green-600">
                    {(selectedService.total_amount || 0).toLocaleString('tr-TR')} {currencySymbols[selectedService.currency] || '₺'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Ödeme Tipi</Label>
                  <p className="capitalize">{selectedService.payment_type || 'Peşin'}</p>
                </div>
              </div>

              {/* Sözleşme Belgeleri */}
              <div className="border-t pt-4">
                <Label className="text-gray-500 flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4" />
                  Sözleşme Belgeleri ({selectedService.contract_documents?.length || 0})
                </Label>
                {selectedService.contract_documents?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedService.contract_documents.map((doc, index) => (
                      <a
                        key={index}
                        href={`${API}${doc.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2 text-sm text-amber-700 hover:bg-amber-100"
                      >
                        <File className="h-4 w-4" />
                        {doc.filename}
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">Belge yok</p>
                )}
              </div>

              {/* Hakedişler */}
              <div className="border-t pt-4">
                <Label className="text-gray-500 flex items-center gap-2 mb-2">
                  <Receipt className="h-4 w-4" />
                  Hakedişler ({selectedService.hakedisler?.length || 0})
                </Label>
                {selectedService.hakedisler?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedService.hakedisler.map((hakedis, index) => (
                      <div
                        key={hakedis.id || index}
                        className="bg-gray-50 rounded-lg border p-3"
                        data-testid={`detail-hakedis-${hakedis.id || index}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-green-700 text-sm">{index + 1}. Hakediş</span>
                          <Badge className={
                            hakedis.durum === 'odendi' ? 'bg-green-100 text-green-700' :
                            hakedis.durum === 'onaylandi' ? 'bg-blue-100 text-blue-700' :
                            hakedis.durum === 'iptal' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }>
                            {hakedis.durum === 'odendi' ? 'Ödendi' :
                             hakedis.durum === 'onaylandi' ? 'Onaylandı' :
                             hakedis.durum === 'iptal' ? 'İptal' : 'Beklemede'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Tarih:</span>
                            <p className="font-medium">
                              {hakedis.tarih ? new Date(hakedis.tarih).toLocaleDateString('tr-TR') : '-'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">Hizmetler:</span>
                            {hakedis.hizmetler && hakedis.hizmetler.length > 0 ? (
                              <div className="space-y-0.5 mt-0.5">
                                {hakedis.hizmetler.map((h, hi) => (
                                  <p key={hi} className="font-medium text-xs">
                                    • {h.ad}{h.miktar ? <span className="text-gray-500"> — {h.miktar}</span> : null}
                                  </p>
                                ))}
                              </div>
                            ) : (
                              <p className="font-medium">{hakedis.miktar || '-'}</p>
                            )}
                          </div>
                          {parseFloat(hakedis.tutar || 0) > 0 && (
                            <div>
                              <span className="text-gray-500">Tutar:</span>
                              <p className="font-semibold text-green-600">
                                {parseFloat(hakedis.tutar || 0).toLocaleString('tr-TR')} {currencySymbols[selectedService.currency] || '₺'}
                              </p>
                            </div>
                          )}
                        </div>
                        {hakedis.aciklama && (
                          <div className="mt-2 text-sm">
                            <span className="text-gray-500">Açıklama:</span>
                            <p className="font-medium">{hakedis.aciklama}</p>
                          </div>
                        )}
                        {hakedis.belgeler && hakedis.belgeler.length > 0 && (
                          <div className="mt-2 pt-2 border-t">
                            <span className="text-xs font-medium text-gray-700 flex items-center gap-1 mb-1">
                              <File className="h-3 w-3 text-amber-600" />
                              Belgeler ({hakedis.belgeler.length})
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {hakedis.belgeler.map((doc, docIdx) => (
                                <a
                                  key={docIdx}
                                  href={doc.url?.startsWith('/api') ? `${BACKEND_URL}${doc.url}` : `${API}${doc.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 bg-amber-50 rounded px-2 py-1 text-xs text-amber-700 hover:bg-amber-100"
                                  data-testid={`detail-hakedis-doc-${hakedis.id || index}-${docIdx}`}
                                >
                                  <File className="h-3 w-3" />
                                  {doc.filename || doc.name || 'Belge'}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">Henüz hakediş girilmemiş</p>
                )}
              </div>


              {/* Notlar */}
              {selectedService.notes && (
                <div className="border-t pt-4">
                  <Label className="text-gray-500">Notlar</Label>
                  <p className="text-sm text-gray-600">{selectedService.notes}</p>
                </div>
              )}

              {/* Sonlandırma Talebi Butonu */}
              {(selectedService.status === 'active' || selectedService.status === 'aktif') && !selectedService.termination_requested && (
                <div className="border-t pt-4">
                  <Button
                    variant="destructive"
                    className="w-full"
                    data-testid="termination-request-btn"
                    onClick={async () => {
                      if (!window.confirm('Bu hizmet alımı için sonlandırma talebi göndermek istediğinize emin misiniz?')) return;
                      try {
                        await axios.put(
                          `${API}/branch/services/${selectedService.id}/termination-request`,
                          {},
                          { headers: getAuthHeaders() }
                        );
                        toast.success('Sonlandırma talebi gönderildi');
                        setSelectedService(null);
                        loadServices();
                      } catch (err) {
                        toast.error('Talep gönderilemedi');
                      }
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Hizmet Alımını Sonlandırma Talebi
                  </Button>
                </div>
              )}
              {selectedService.termination_requested && (
                <div className="border-t pt-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-700 font-medium flex items-center gap-2">
                      <X className="h-4 w-4" />
                      Sonlandırma talebi gönderildi
                    </p>
                    <p className="text-red-600 text-sm mt-1">
                      {selectedService.termination_requested_by} - {selectedService.termination_requested_at ? new Date(selectedService.termination_requested_at).toLocaleString('tr-TR') : ''}
                    </p>
                  </div>
                </div>
              )}
            </div>
              </TabsContent>
              <TabsContent value="gunluk">
                <div className="py-4">
                  <ServiceDailyReports serviceId={selectedService.id} canEdit={!readOnly} />
                </div>
              </TabsContent>
              <TabsContent value="notlar">
                <div className="py-4">
                  <ServiceNotes serviceId={selectedService.id} />
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Yeni Hakediş Modal */}
      <Dialog open={showHakedisModal} onOpenChange={setShowHakedisModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-green-600" />
              Yeni Hakediş Ekle
            </DialogTitle>
          </DialogHeader>
          
          {selectedServiceForHakedis && (
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-sm text-amber-700">
                  <strong>{selectedServiceForHakedis.service_name}</strong> hizmetine hakediş ekleniyor
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Tarih *</Label>
                <Input
                  type="date"
                  value={newHakedis.tarih}
                  onChange={(e) => setNewHakedis(prev => ({ ...prev, tarih: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Alınan Hizmetler</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1 h-7"
                    onClick={addHizmet}
                    data-testid="add-hizmet-btn"
                  >
                    <Plus className="h-3 w-3" />
                    Hizmet Ekle
                  </Button>
                </div>
                <div className="space-y-2">
                  {newHakedis.hizmetler.map((hizmet, index) => (
                    <div key={index} className="flex items-center gap-2" data-testid={`hizmet-row-${index}`}>
                      <Input
                        placeholder="Hizmet adı (Örn: Beton dökümü)"
                        value={hizmet.ad}
                        onChange={(e) => updateHizmet(index, 'ad', e.target.value)}
                        className="flex-1"
                        data-testid={`hizmet-ad-${index}`}
                      />
                      <Input
                        placeholder="Miktar (Örn: 100 m²)"
                        value={hizmet.miktar}
                        onChange={(e) => updateHizmet(index, 'miktar', e.target.value)}
                        className="w-36"
                        data-testid={`hizmet-miktar-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                        onClick={() => removeHizmet(index)}
                        disabled={newHakedis.hizmetler.length === 1}
                        data-testid={`remove-hizmet-${index}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400">Birden fazla hizmet alındıysa her birini ayrı ekleyin (ad + miktar).</p>
              </div>
              
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea
                  placeholder="Hakediş ile ilgili açıklama..."
                  value={newHakedis.aciklama}
                  onChange={(e) => setNewHakedis(prev => ({ ...prev, aciklama: e.target.value }))}
                  rows={3}
                />
              </div>
              
              {/* Belgeler */}
              <div className="space-y-2">
                <Label>Belgeler</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => document.getElementById('hakedis-file-input').click()}
                    disabled={uploadingFile}
                  >
                    {uploadingFile ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Dosya Ekle
                  </Button>
                  <input
                    id="hakedis-file-input"
                    type="file"
                    className="hidden"
                    onChange={handleHakedisFileUpload}
                  />
                </div>
                {newHakedis.belgeler.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newHakedis.belgeler.map((doc, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-2 bg-green-50 rounded px-2 py-1 text-sm"
                      >
                        <File className="h-3 w-3 text-green-600" />
                        <span className="text-green-700">{doc.filename}</span>
                        <button
                          type="button"
                          onClick={() => removeHakedisFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowHakedisModal(false)}
                >
                  İptal
                </Button>
                <Button
                  onClick={handleSaveHakedis}
                  disabled={savingHakedis}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingHakedis ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Hakediş Ekle
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Kişi Seçim Modalı */}
      <Dialog open={showUserSelectionModal} onOpenChange={setShowUserSelectionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-600" />
              İş Paneline Kişi Seç
            </DialogTitle>
          </DialogHeader>
          
          {selectedServiceForUsers && (
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-sm text-amber-700">
                  <strong>{selectedServiceForUsers.service_name}</strong> hizmeti için İş Paneli'nde görebilecek kişileri seçin. Seçilen her kişi için sözleşme belgelerini görüp göremeyeceğini ayrıca belirleyebilirsiniz.
                </p>
              </div>
              
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-500">Kullanıcılar yükleniyor...</span>
                </div>
              ) : workPanelUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p>Bu şubede İş Paneli erişimi olan kullanıcı bulunamadı</p>
                </div>
              ) : (
                <>
                  {/* Tümünü seç/kaldır */}
                  <div className="flex items-center justify-between pb-2 border-b">
                    <span className="text-sm text-gray-600">
                      {selectedUsers.length} / {workPanelUsers.length} kişi seçili
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={selectAllUsers}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Tümünü Seç
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={deselectAllUsers}
                        className="text-gray-600 hover:text-gray-700"
                      >
                        Temizle
                      </Button>
                    </div>
                  </div>
                  
                  {/* Kullanıcı listesi */}
                  <ScrollArea className="h-[300px] pr-4">
                    <div className="space-y-2">
                      {workPanelUsers.map((user) => (
                        <div
                          key={user.id}
                          className={`rounded-lg border transition-colors ${
                            selectedUsers.includes(user.id)
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div
                            className="flex items-center gap-3 p-3 cursor-pointer"
                            onClick={() => toggleUserSelection(user.id)}
                            data-testid={`user-checkbox-${user.id}`}
                          >
                            <Checkbox
                              checked={selectedUsers.includes(user.id)}
                              onCheckedChange={() => toggleUserSelection(user.id)}
                              className="data-[state=checked]:bg-blue-600"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{user.name}</p>
                              {user.position_name && (
                                <p className="text-sm text-gray-500">{user.position_name}</p>
                              )}
                              {user.email && (
                                <p className="text-xs text-gray-400">{user.email}</p>
                              )}
                            </div>
                            {selectedUsers.includes(user.id) && (
                              <CheckCircle className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          {/* Sözleşme belgesi görüntüleme yetkisi (sadece seçili kişiler için) */}
                          {selectedUsers.includes(user.id) && (
                            <div
                              className="flex items-center gap-2 px-3 pb-3 pl-10 cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); toggleContractViewer(user.id); }}
                              data-testid={`contract-viewer-toggle-${user.id}`}
                            >
                              <Checkbox
                                checked={contractViewers.includes(user.id)}
                                onCheckedChange={() => toggleContractViewer(user.id)}
                                className="data-[state=checked]:bg-amber-600 border-amber-400"
                              />
                              <span className="text-xs text-amber-700 flex items-center gap-1">
                                <FileText className="h-3.5 w-3.5" />
                                Sözleşme belgelerini görebilir
                              </span>
                            </div>
                          )}
                          {/* Günlük çalışan listesi/rapor sorumlusu (sadece seçili kişiler için) */}
                          {selectedUsers.includes(user.id) && (
                            <div
                              className="flex items-center gap-2 px-3 pb-3 pl-10 cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); toggleDailyReportUser(user.id); }}
                              data-testid={`daily-report-user-toggle-${user.id}`}
                            >
                              <Checkbox
                                checked={dailyReportUsers.includes(user.id)}
                                onCheckedChange={() => toggleDailyReportUser(user.id)}
                                className="data-[state=checked]:bg-green-600 border-green-400"
                              />
                              <span className="text-xs text-green-700 flex items-center gap-1">
                                <ClipboardList className="h-3.5 w-3.5" />
                                Günlük çalışan listesi & rapor girebilir
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </>
              )}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowUserSelectionModal(false)}
                >
                  İptal
                </Button>
                <Button
                  onClick={handleSaveSelectedUsers}
                  disabled={savingUsers || loadingUsers}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {savingUsers ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <UserPlus className="h-4 w-4 mr-2" />
                  )}
                  {selectedUsers.length === 0 ? 'Paylaşımı Kaldır' : `${selectedUsers.length} Kişiyle Paylaş`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchServices;
